import type { ReactNode } from 'react';

/** 设置分组只描述导航，不管理业务配置或保存状态。 */
export interface SettingsNavigationGroup<T extends string> {
  key: string;
  label: string;
  items: { key: T; label: string; icon: ReactNode }[];
}

/**
 * 弹窗和嵌入设置共用导航：宽屏竖排分组，窄屏使用原生分组选择器。
 * @param active 当前页面键；由设置容器统一管理草稿，切页不丢失未保存配置。
 * @param onSelect 请求切页，不触发保存或配置写入。
 */
export default function SettingsNavigation<T extends string>({ groups, active, label, onSelect }: {
  groups: SettingsNavigationGroup<T>[];
  active: T;
  label: string;
  onSelect: (key: T) => void;
}) {
  const visibleGroups = groups.filter(group => group.items.length > 0);
  return (
    <nav aria-label={label} className="non-draggable shrink-0 border-b border-claude-border bg-claude-surface/50 dark:border-claude-darkBorder dark:bg-claude-darkSurface/50 sm:w-48 sm:overflow-y-auto sm:border-b-0 sm:border-r lg:w-56">
      <div className="p-3 sm:hidden">
        <select aria-label={label} value={active} onChange={event => {
          const item = visibleGroups.flatMap(group => group.items).find(entry => entry.key === event.target.value);
          if (item) onSelect(item.key);
        }} className="w-full rounded-lg border border-claude-border bg-claude-bg px-3 py-2 text-sm text-claude-text dark:border-claude-darkBorder dark:bg-claude-darkBg dark:text-claude-darkText">
          {visibleGroups.map(group => <optgroup key={group.key} label={group.label}>
            {group.items.map(item => <option key={item.key} value={item.key}>{item.label}</option>)}
          </optgroup>)}
        </select>
      </div>
      <div className="hidden space-y-6 px-3 py-6 sm:block">
        {visibleGroups.map(group => (
          <section key={group.key} aria-label={group.label}>
            <h3 className="mb-2 px-3 text-xs font-medium text-claude-textSecondary dark:text-claude-darkTextSecondary">{group.label}</h3>
            <div className="space-y-1">
              {group.items.map(item => (
                <button key={item.key} type="button" aria-current={active === item.key ? 'page' : undefined}
                  onClick={() => onSelect(item.key)}
                  className={`flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left text-sm font-medium transition-colors motion-reduce:transition-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-claude-accent ${active === item.key
                    ? 'bg-claude-surfaceHover text-claude-accent dark:bg-claude-darkSurfaceHover'
                    : 'text-claude-text hover:bg-claude-surfaceHover dark:text-claude-darkText dark:hover:bg-claude-darkSurfaceHover'}`}>
                  <span aria-hidden="true" className="shrink-0">{item.icon}</span>
                  <span>{item.label}</span>
                </button>
              ))}
            </div>
          </section>
        ))}
      </div>
    </nav>
  );
}
